import React from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { useProgramPanel } from 'contexts/programPanelContext';
import { timelineApi } from 'api/timeline';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { faltPaginatedTimelineDataAndSort, groupTimelineData } from 'components/_common/timeline/timeline.utils';
import { TimelineEventItem } from 'types/Timeline';
import { Loader } from 'components/_new/Loader';
import { Button } from 'components/_new/Button';
import { TimelineEventCard } from 'components/_common/timeline/TimelineEventCard';
import { Link, useNavigate } from 'react-router-dom';
import { MessageBox } from 'components/_new/MessageBox';
import { EmptyState } from 'utils/emptyState';

export const ProgramTimelineList = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: programData, panelPath } = useProgramPanel();

	const {
		data: flatData,
		refetch,
		isLoading,
	} = useQuery(
		['TIMELINE_EVENTS', { programId: Number(programData?.id) }],
		() => timelineApi.program.getTimelineEvents(Number(programData?.id)),
		{
			enabled: true,
		}
	);

	const grouppedData = groupTimelineData(flatData);

	if (isLoading && !grouppedData.length) {
		return <Loader />;
	}

	return (
		<>
			<Helmet title={t('timeline.title')} />
			<SubPageTitle
				title={t('timeline.title')}
				right={
					programData?.eventsEnabled
						? () => (
								<Button
									label={t('timeline.programPanel.addTitle')}
									icon="plus"
									onClick={() =>
										navigate({
											pathname: `/${panelPath}/timeline/add`,
										})
									}
								/>
						  )
						: undefined
				}
			/>
			{!programData?.eventsEnabled ? (
				<MessageBox
					message={
						<Trans
							t={t}
							i18nKey="timeline.programPanel.disabledMessage"
							components={[<Link to={{ pathname: `/${panelPath}/config` }} />]}
						/>
					}
				/>
			) : (
				<div className="flex flex-column gap-2 justify-content-center align-items-center">
					{grouppedData.length > 0 ? (
						<>
							{grouppedData.map(({ key, name, items }) => {
								return (
									<div key={key} className="w-full">
										<div className="text-primary text-lg my-2">{name}</div>
										<ul className="flex flex-column gap-2 list-none">
											{items.map((item) => {
												return (
													<TimelineEventCard
														key={item.key}
														data={item}
														onTitleClick={() => {
															navigate({
																pathname: `/${panelPath}/timeline/details/${item.eventId}`,
															});
														}}
														actions={[
															{
																key: 'edit',
																label: t('actions.edit'),
																variant: 'primary-outlined',
																variantSize: 'sm',
																icon: 'pencil',
																onClick: () => {
																	navigate({
																		pathname: `/${panelPath}/timeline/edit/${item.eventId}`,
																	});
																},
															},
														]}
														additionalInfo
													/>
												);
											})}
										</ul>
									</div>
								);
							})}
						</>
					) : (
						<EmptyState title={t('timeline.noEventsAction')} />
					)}
				</div>
			)}
		</>
	);
};
